import { shipmentId } from "@cdc-sccr/shared"
import { escapeCSVCell } from "@/lib/csv"
import { type BillingStatus, BILLING_STATUS_LABELS } from "@/lib/billing-status"
import { DELIVERY_NOTE_STATUS_LABEL, type DeliveryNoteStatus } from "./ShipmentDetailUtils"

export interface BillingExportRow {
  internalId: number
  clientName: string | null
  scheduledOriginArrivalAt: string | null
  driverNames: string | null
  mobileNumbers: string | null
  firstTripMobileNumber: number | null
  operatorNames: string | null
  truckPlates: string | null
  trailerPlates: string | null
  senderName: string | null
  senderCityName: string | null
  recipientName: string | null
  recipientCityName: string | null
  deliveryNoteNumber: string | null
  deliveryNoteStatus: DeliveryNoteStatus
  unloadingWeightKg: string | null
  billingStatus: BillingStatus | null
  billingTags: string | null
  billingNotes: string | null
  notes: string | null
}

function joinPipeValues(value: string | null | undefined): string {
  return value?.split("||").map(item => item.trim()).filter(Boolean).join(", ") ?? ""
}

export function selectBillingExportRows<T extends { id: string }>(
  rows: ReadonlyMap<string, T>,
  selectedIds: ReadonlySet<string>,
): T[] {
  return [...rows.values()].filter(row => selectedIds.has(row.id))
}

export function buildBillingCsv(rows: BillingExportRow[]): string {
  const headers = [
    "Nro Carga", "Cliente", "Fecha", "Choferes", "Nro Movil", "Responsable móvil", "Patente Camion", "Patente Semi",
    "Remitente → Ciudad", "Destinatario → Ciudad", "Remito", "Estado remito", "Toneladas descargadas",
    "Estado facturación", "Etiquetas", "Observación facturación", "Observación carga",
  ]
  const lines = [headers.map(escapeCSVCell).join(",")]
  for (const r of rows) {
    lines.push([
      shipmentId(r.internalId),
      r.clientName ?? "",
      r.scheduledOriginArrivalAt ? new Date(r.scheduledOriginArrivalAt).toLocaleDateString("es-AR") : "",
      joinPipeValues(r.driverNames),
      joinPipeValues(r.mobileNumbers) || (r.firstTripMobileNumber != null ? String(r.firstTripMobileNumber) : ""),
      joinPipeValues(r.operatorNames),
      joinPipeValues(r.truckPlates),
      joinPipeValues(r.trailerPlates),
      [r.senderName, r.senderCityName].filter(Boolean).join(" → "),
      [r.recipientName, r.recipientCityName].filter(Boolean).join(" → "),
      r.deliveryNoteNumber ?? "",
      DELIVERY_NOTE_STATUS_LABEL[r.deliveryNoteStatus ?? "normal"],
      r.unloadingWeightKg ? (parseFloat(r.unloadingWeightKg) / 1000).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "",
      BILLING_STATUS_LABELS[r.billingStatus ?? "pendiente"],
      r.billingTags?.split("||").map(t => t.trim()).filter(Boolean).join(", ") ?? "",
      r.billingNotes ?? "",
      r.notes ?? "",
    ].map(v => escapeCSVCell(String(v))).join(","))
  }
  return lines.join("\r\n")
}
